import { z } from 'zod';

import { Box, Form, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import type {
	MacroDealerEvent,
	MacroInformation,
	MacroServiceInfo,
	MacroSocialLink,
	MacroTeamMember,
} from '../types';

// Read-only placeholder schema — no editable fields
const readOnlySchema = z.object({});

interface ContextBlockProps {
	name: string;
	title: string;
	description: string;
	children?: React.ReactNode;
	comingSoon?: boolean;
}

function ContextBlock({
	name,
	title,
	description,
	children,
	comingSoon = false,
}: ContextBlockProps): React.ReactNode {
	return (
		<Box padding='24' background='secondary' rounding='12'>
			<div data-settings-disabled>
				<Form
					name={`ai-bdc-context-${name}`}
					schema={readOnlySchema}
					defaultValues={{}}
					onSubmit={async () => ({ isSuccess: false })}
					title={title}
					titleLevel='subgroup'
					titleWeight='semibold'
					titleMaxWidth='full'
					description={description}
					tag={comingSoon ? { children: 'Coming soon', size: 'small' } : undefined}>
					{children}
				</Form>
			</div>
		</Box>
	);
}

function ComingSoonPlaceholder(): React.ReactNode {
	return (
		<Box padding='16' background='primary' rounding='8'>
			<Text size='bodySmall' color='tertiary'>
				This section will be automatically populated from your website.
			</Text>
		</Box>
	);
}

function NotScrapedPlaceholder(): React.ReactNode {
	return (
		<Box padding='16' background='primary' rounding='8'>
			<Text size='bodySmall' color='tertiary'>
				No data found on your website for this section.
			</Text>
		</Box>
	);
}

/**
 * Three states for macro data:
 *   undefined — query still loading (or first render before fetch resolves)
 *   null      — fetch resolved; Firestore doc does not exist (never scraped)
 *   object    — fetch resolved with scraped data
 */
type MacroInfoState = MacroInformation | null | undefined;

export function AboutSection({ macroInfo }: { macroInfo?: MacroInfoState }): React.ReactNode {
	const socialLinks: MacroSocialLink[] = macroInfo?.socialLinks ?? [];

	return (
		<Stack itemsSpacing='16'>
			<ContextBlock
				name='about-us'
				title='About us'
				description="Your business story, mission, and history — pulled from your website's about page."
				comingSoon={macroInfo === undefined}>
				{macroInfo === undefined ? (
					<ComingSoonPlaceholder />
				) : macroInfo?.aboutUs ? (
					<Box padding='16' background='primary' rounding='8'>
						<Text size='bodySmall' color='primary'>
							{macroInfo.aboutUs}
						</Text>
					</Box>
				) : (
					<NotScrapedPlaceholder />
				)}
				{socialLinks.length > 0 && (
					<Stack itemsSpacing='8'>
						<Text size='bodySmall' weight='semibold'>
							Social links
						</Text>
						{socialLinks.map((link: MacroSocialLink, i: number) => (
							<Group key={i} itemsSpacing='8'>
								<Group.Item>
									<Text size='bodySmall' color='secondary'>
										{link.platform}:
									</Text>
								</Group.Item>
								<Group.Item>
									<Text size='bodySmall' color='primary'>
										{link.url}
									</Text>
								</Group.Item>
							</Group>
						))}
					</Stack>
				)}
			</ContextBlock>
			<ContextBlock
				name='locations'
				title='Locations'
				description='Business addresses, maps, and location-specific details.'
				comingSoon>
				<ComingSoonPlaceholder />
			</ContextBlock>
		</Stack>
	);
}

export function TeamSection({ macroInfo }: { macroInfo?: MacroInfoState }): React.ReactNode {
	const members: MacroTeamMember[] = macroInfo?.meetOurTeam ?? [];

	return (
		<ContextBlock
			name='team'
			title='Team'
			description='Staff directory, bios, and roles — so the AI can reference the right people when a lead asks to speak with someone specific.'
			comingSoon={macroInfo === undefined}>
			{macroInfo === undefined ? (
				<ComingSoonPlaceholder />
			) : members.length > 0 ? (
				<Stack itemsSpacing='8'>
					{members.map((member: MacroTeamMember, i: number) => (
						<Box key={i} padding='12' background='primary' rounding='8'>
							<Group itemsSpacing='8'>
								<Group.Item>
									<Text size='bodySmall' weight='semibold'>
										{member.name}
									</Text>
								</Group.Item>
								<Group.Item>
									<Text size='bodySmall' color='secondary'>
										{member.role}
									</Text>
								</Group.Item>
							</Group>
						</Box>
					))}
				</Stack>
			) : (
				<NotScrapedPlaceholder />
			)}
		</ContextBlock>
	);
}

export function TestimonialsSection({
	macroInfo,
}: {
	macroInfo?: MacroInfoState;
}): React.ReactNode {
	const testimonials: string[] = macroInfo?.testimonials ?? [];

	return (
		<ContextBlock
			name='testimonials'
			title='Reviews & testimonials'
			description='Customer reviews and testimonials from your website. The AI can reference real feedback when building trust with leads.'
			comingSoon={macroInfo === undefined}>
			{macroInfo === undefined ? (
				<ComingSoonPlaceholder />
			) : testimonials.length > 0 ? (
				<Stack itemsSpacing='8'>
					{testimonials.map((testimonial: string, i: number) => (
						<Box key={i} padding='12' background='primary' rounding='8'>
							<Text size='bodySmall' color='primary'>
								"{testimonial}"
							</Text>
						</Box>
					))}
				</Stack>
			) : (
				<NotScrapedPlaceholder />
			)}
		</ContextBlock>
	);
}

export function BlogSection(): React.ReactNode {
	return (
		<ContextBlock
			name='blog'
			title='Blog & articles'
			description='Blog posts, news, and educational content. Gives the AI rich context to answer product and lifestyle questions.'
			comingSoon>
			<ComingSoonPlaceholder />
		</ContextBlock>
	);
}

export function EventsSection({ macroInfo }: { macroInfo?: MacroInfoState }): React.ReactNode {
	const events: MacroDealerEvent[] = macroInfo?.events ?? [];

	return (
		<ContextBlock
			name='events'
			title='Events & calendar'
			description='Upcoming events, demos, and community gatherings. The AI can invite leads to relevant events.'
			comingSoon={macroInfo === undefined}>
			{macroInfo === undefined ? (
				<ComingSoonPlaceholder />
			) : events.length > 0 ? (
				<Stack itemsSpacing='8'>
					{events.map((event: MacroDealerEvent, i: number) => (
						<Box key={i} padding='12' background='primary' rounding='8'>
							<Stack itemsSpacing='4'>
								<Group itemsSpacing='8'>
									<Group.Item>
										<Text size='bodySmall' weight='semibold'>
											{event.name}
										</Text>
									</Group.Item>
									{event.date && (
										<Group.Item>
											<Text size='bodySmall' color='secondary'>
												{event.date}
											</Text>
										</Group.Item>
									)}
								</Group>
								{event.description && (
									<Text size='bodySmall' color='secondary'>
										{event.description}
									</Text>
								)}
							</Stack>
						</Box>
					))}
				</Stack>
			) : (
				<NotScrapedPlaceholder />
			)}
		</ContextBlock>
	);
}

export function PromotionsSection(): React.ReactNode {
	return (
		<ContextBlock
			name='promotions'
			title='Promotions & specials'
			description='Active sales, promotions, and limited-time offers. Keeps the AI current on deals it can mention to leads.'
			comingSoon>
			<ComingSoonPlaceholder />
		</ContextBlock>
	);
}

export function ServicesSection({ macroInfo }: { macroInfo?: MacroInfoState }): React.ReactNode {
	const services: MacroServiceInfo[] = macroInfo?.highLevelServiceInfo ?? [];

	return (
		<ContextBlock
			name='services'
			title='Service & parts'
			description='Service department capabilities, parts ordering, and maintenance programs. Helps the AI assist with post-sale and service inquiries.'
			comingSoon={macroInfo === undefined}>
			{macroInfo === undefined ? (
				<ComingSoonPlaceholder />
			) : services.length > 0 ? (
				<Stack itemsSpacing='8'>
					{services.map((service: MacroServiceInfo, i: number) => (
						<Box key={i} padding='12' background='primary' rounding='8'>
							<Stack itemsSpacing='4'>
								<Text size='bodySmall' weight='semibold'>
									{service.serviceName}
								</Text>
								{service.serviceDescription && (
									<Text size='bodySmall' color='secondary'>
										{service.serviceDescription}
									</Text>
								)}
							</Stack>
						</Box>
					))}
				</Stack>
			) : (
				<NotScrapedPlaceholder />
			)}
		</ContextBlock>
	);
}

export function FinancingSection(): React.ReactNode {
	return (
		<ContextBlock
			name='financing'
			title='Financing & insurance'
			description='Financing options, credit application info, and insurance products available through your business.'
			comingSoon>
			<ComingSoonPlaceholder />
		</ContextBlock>
	);
}

export function BrandsSection(): React.ReactNode {
	return (
		<ContextBlock
			name='brands'
			title='Brands & product lines'
			description='The manufacturers and product lines you carry — so the AI knows your catalog and can speak knowledgeably about each brand.'
			comingSoon>
			<ComingSoonPlaceholder />
		</ContextBlock>
	);
}

export function PoliciesSection({ macroInfo }: { macroInfo?: MacroInfoState }): React.ReactNode {
	return (
		<ContextBlock
			name='policies'
			title='Policies & FAQs'
			description='Return policies, warranty information, trade-in guidelines, and frequently asked questions from your website.'
			comingSoon={macroInfo === undefined}>
			{macroInfo === undefined ? (
				<ComingSoonPlaceholder />
			) : macroInfo?.returnPolicy ? (
				<Box padding='16' background='primary' rounding='8'>
					<Text size='bodySmall' color='primary'>
						{macroInfo.returnPolicy}
					</Text>
				</Box>
			) : (
				<NotScrapedPlaceholder />
			)}
		</ContextBlock>
	);
}
